import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { clientEntriesSelector } from "../../store/selectors/clientEntries";
import { fetchReferenceDataColumns } from "../clientRequests";
import { referenceDataFileListSelector } from "../../store/selectors/referenceData";
import { matchById, matchByGuid, compose, I } from "../utils";
import { usePrevious } from "./usePrevious";
import { referenceDataEndpointSelector, clientTypeSelector } from "../../store/selectors/clientSettings";
import { useReferenceDataInDispatchSelector } from "../../store/selectors/entities/form";

// currently just an in memory cache, can be swapped out for another solution
const refCache = {};

// TODO: making these structured remote data types could be useful down the road
const NOTASKED = 'notasked';
const LOADING = 'loading';
const SUCCESS = 'success';
const FAILURE = 'failure';

// extracts the columns data from response and removes the empty first element
// TODO: we should ideally do a lot more work here to make eventual row lookups faster, but
// ideally it should handle very large files, so possibly moving processing to webworker/async
// const fixColumns = ({ columns }) =>
//   columns.map(col => {
//     col.shift() // removes the empty first item: the mutable shift is faster than a splice/spread
//     return col
//   })

const numbersOnly = value => Number(String(value).replace(/[^.\d]/g, ''));
const clamp = (num, min, max) => Math.min(Math.max(num, min), max);
const clampValue = ({
  minimum = 0,
  maximum = Infinity,
  value,
  type
}) => {
  if (type == 'Rating' || type == 'Number') {
    const filteredValue = numbersOnly(value);
    if (minimum !== null && maximum !== null) {
      if (type === 'Rating') {
        return clamp(filteredValue, 1, 5);
      } else {
        return clamp(filteredValue, minimum, maximum);
      }
    }
    return filteredValue;
  }
  return value;
};
const getColumns = ({
  columns
}) => columns;

// columns is like a Non-Empty-List here: its length is predictably always/only 0 when not loaded
const useReferenceData = reference_data_id => {
  const refDataFiles = useSelector(referenceDataFileListSelector) || [];
  const refDataEndpoint = useSelector(referenceDataEndpointSelector);
  const metaData = refDataFiles.find(matchById(reference_data_id)) || {}; // getting meta data
  const {
    columns = [],
    request: existingRequest
  } = refCache[reference_data_id] || {};
  const [fileState, updateFileState] = useState(() => {
    if (columns.length) {
      return SUCCESS;
    }
    if (existingRequest) {
      return LOADING;
    }
    return NOTASKED;
  });
  if (reference_data_id && fileState === NOTASKED) {
    refCache[reference_data_id] = {
      columns,
      request: fetchReferenceDataColumns(refDataEndpoint, reference_data_id).then(({
        data
      }) => data).then(getColumns)
    };
  }

  // handling requests
  useEffect(() => {
    let cancelRequest = false;

    // do nothing if no file id, or we already have columns
    if (!reference_data_id || fileState === SUCCESS) {
      return;
    }
    const handleError = () => {
      if (cancelRequest) {
        return;
      }
      updateFileState(FAILURE);
    };
    const addData = columns => {
      if (cancelRequest) {
        return;
      }
      refCache[reference_data_id] = {
        columns,
        request: null
      };
      updateFileState(SUCCESS);
      return columns;
    };
    const currentRequest = existingRequest ? existingRequest : refCache[reference_data_id].request;
    currentRequest.then(addData).catch(handleError);
    return function cleanup() {
      cancelRequest = true;
    };
  }, [reference_data_id]);
  return {
    ...metaData,
    fileState,
    columns
  };
};

// returns an array of simple strings
export const useReferenceDataColumn = ({
  reference_data_id,
  reference_data_column,
  default_options = []
}) => {
  const referenceData = useReferenceData(reference_data_id);
  const {
    reference_columns = [],
    columns
  } = referenceData;
  if (!reference_data_id || !reference_data_column) {
    return {
      ...referenceData,
      column: default_options
    };
  }
  if (!columns || !columns.length) {
    return {
      ...referenceData,
      column: []
    };
  }
  const colIndex = reference_columns.indexOf(reference_data_column);
  return {
    ...referenceData,
    column: columns[colIndex] || []
  };
};

// returns an array of options objects (common props like label/text/etc.)
export const useReferenceDataOptions = ({
  reference_data_id,
  reference_data_column,
  default_options = [],
  removeDuplicates = false,
  currentlySelectedOptions = []
}) => {
  const referenceData = useReferenceDataColumn({
    reference_data_id,
    reference_data_column,
    default_options
  });

  // we shouldn't alter the default options, so we just return them
  if (!reference_data_column) {
    return {
      ...referenceData,
      options: default_options
    };
  }
  const {
    column: flatCollumn
  } = referenceData;

  // Unique the options
  let uniqueData = flatCollumn.filter((v, i, a) => a.indexOf(v) === i);
  let options = uniqueData.map((text, position) => {
    return {
      text,
      value: text,
      position,
      label: text
    };
  });
  if (removeDuplicates) {
    // filter out any options that have already been selected
    options = options.filter(option => !currentlySelectedOptions.includes(option.value));
  }
  return {
    ...referenceData,
    options
  };
};
const lookupReferenceValue = (referenceFieldValue, sourceReferenceColumn, columns, reference_columns, reference_data_column, type, minimum, maximum) => {
  if (!reference_columns || !columns.length || !referenceFieldValue || !reference_data_column) {
    return false;
  }
  const sourceColumnIndex = reference_columns.indexOf(sourceReferenceColumn);
  const dependentColumnIndex = reference_columns.indexOf(reference_data_column);
  const sourceRowIndex = (columns[sourceColumnIndex] || []).indexOf(String(referenceFieldValue) // I think this coercion is right, otherwise it won't find numbers
  );
  if (sourceRowIndex === -1) {
    return false;
  }
  const dependentValue = columns[dependentColumnIndex][sourceRowIndex];
  return clampValue({
    value: dependentValue,
    type,
    maximum,
    minimum
  });
};

// HOC version to wrap field components, potentially intercepting or updating their values
export const withDependentReferenceData = Component => props => {
  const {
    dependent_entry,
    reference_data_id,
    reference_data_column,
    value,
    update,
    type,
    minimum,
    maximum
  } = props || {};
  const {
    guid = null
  } = dependent_entry || {};

  // get the dependent entry's value
  const {
    value: referenceFieldValue,
    reference_data_column: sourceReferenceColumn
  } = useSelector(clientEntriesSelector).find(matchByGuid(guid)) || {};
  const clientType = useSelector(clientTypeSelector);
  const useReferenceDataInDispatch = useSelector(useReferenceDataInDispatchSelector);
  const [edited, setEditedState] = useState(false);
  const prevRefValue = usePrevious(referenceFieldValue);
  const {
    columns,
    reference_columns
  } = useReferenceData(reference_data_id);
  const memoProps = [referenceFieldValue, sourceReferenceColumn, columns, reference_columns, reference_data_column, type, minimum, maximum];

  // lookups could get reaaaaally expensive
  const dependentValue = useMemo(() => lookupReferenceValue(...memoProps), memoProps);

  // update the allowed value initially or if the parent column value changes
  useEffect(() => {
    const sourceValChange = prevRefValue !== undefined && prevRefValue !== referenceFieldValue;
    const hasWrongValue = value !== dependentValue && !edited;
    // no op until these are available
    if (reference_columns && columns.length && referenceFieldValue && dependentValue !== false) {
      if (!sourceValChange && value && value !== dependentValue) {
        // If the value is updated manually by the user set edited to true to prevent the value from being overwritten
        setEditedState(true);
      } else if (sourceValChange || hasWrongValue) {
        // Source value changed or the field has the wrong value, update the value to be consistent with the source value
        update(dependentValue, {
          // We pass in an object to the 2nd param containing the default attribute if the field is a Link,
          // because the LinkPreview component requires the url to be stored in the default property
          ...(type === 'Link' && {
            default: dependentValue
          })
        });
      }
    }
  }, [reference_data_column, prevRefValue, referenceFieldValue, columns]);

  // If we are in the Dispatch Client, we want the user to be able to input a value into a text field
  // even if it's a dependent reference data field and the user selected NO to the "Would you like to
  // use Reference Data popup"
  const overrideLoadingText = clientType === 'dispatch' && !useReferenceDataInDispatch;
  // If we are fetching ref data, display "Loading..." text in text fields that have a dependent entry
  const getValue = () => {
    let valueToDisplay = value;
    if (type === 'Text' && !overrideLoadingText) {
      valueToDisplay = 'Loading...';
    }
    return valueToDisplay;
  };
  const extraProps =
  // TODO: A better approach could be to use an "isFetching" flag to determine whether or not to display the loading text
  dependent_entry && (!reference_columns || !columns.length) ? {
    readOnly: !overrideLoadingText,
    errors: '',
    value: getValue()
  } : {};
  const overrideProps = {
    ...props,
    ...extraProps
  };
  return <Component {...overrideProps} />;
};

//Returns an array containing the filtered options and the dependent value used for reference data.
const lookupReferenceOptions = (referenceFieldValue, sourceReferenceColumn, columns, reference_columns, reference_data_column) => {
  if (!reference_columns || !columns.length || !referenceFieldValue || !reference_data_column) {
    return false;
  }
  const sourceColumnIndex = reference_columns.indexOf(sourceReferenceColumn);
  const dependentColumnIndex = reference_columns.indexOf(reference_data_column);
  const sourceRowIndex = (columns[sourceColumnIndex] || []).indexOf(String(referenceFieldValue) // I think this coercion is right, otherwise it won't find numbers
  );
  const dependentValue = columns[dependentColumnIndex][sourceRowIndex];
  const sourceRowIndexes = columns[sourceColumnIndex].reduce(
  // Get the index of every occurence of referenceFieldValue (no matter the size) and return a new array with all those indexes
  (acc, value, index) => {
    if (value === referenceFieldValue) {
      acc.push(index);
    }
    return acc;
  }, []);
  // Get all the dependent options
  const dependentOptions = sourceRowIndexes.reduce((acc, index) => {
    const value = columns[dependentColumnIndex][index];
    acc.push({
      value: value,
      label: value
    });
    return acc;
  }, []);

  // Filter the dependentOptions by the unique key
  const dependentFilterOptions = dependentOptions.filter((v, i, a) => a.findIndex(v2 => ['value'].every(k => v2[k] === v[k])) === i, []);
  return {
    dependentFilterOptions,
    dependentValue
  };
};

//Filter the Single Choices Dropdown options based on the dependent value instead of returning only one option in the dropdowns.
//We remove the selectedOption value from props as it is not necessary as we're setting a specific value -- in some situations having this value can lead to the wrong option being selected.
export const withDependentReferenceDataOptions = Component => ({
  selectedOption,
  ...props
}) => {
  const {
    dependent_entry,
    reference_data_id,
    reference_data_column,
    update
  } = props || {};
  const {
    guid = null
  } = dependent_entry || {};

  // Return immediately if we are a parent source field
  if (dependent_entry == undefined) {
    return <Component {...props} />;
  }

  // get the dependent entry's value
  const {
    value: referenceFieldValue,
    reference_data_column: sourceReferenceColumn
  } = useSelector(clientEntriesSelector).find(matchByGuid(guid)) || {};
  const prevRefValue = usePrevious(referenceFieldValue);
  const {
    columns,
    reference_columns
  } = useReferenceData(reference_data_id);
  const memoProps = [referenceFieldValue, sourceReferenceColumn, columns, reference_columns, reference_data_column];

  // lookups could get reaaaaally expensive
  const {
    dependentFilterOptions = [],
    dependentValue
  } = useMemo(() => lookupReferenceOptions(...memoProps), memoProps);
  useEffect(() => {
    const sourceValChange = prevRefValue !== referenceFieldValue;
    if (reference_columns && columns.length && referenceFieldValue && dependentValue !== false) {
      if (sourceValChange) {
        // Source value changed or the field has the wrong value, update the value to be consistent with the source value
        update(dependentValue);
      }
    }
  }, [reference_data_column, prevRefValue, referenceFieldValue, columns]);
  const overrideProps = {
    ...props,
    options: dependentFilterOptions
  };
  return <Component {...overrideProps} />;
};

// for components that use something other than value as their value
export const _withDependentReferenceDataCustomField = (valueName, validateValue = I) => Component => props => {
  const {
    value,
    ...otherProps
  } = props;
  const altProps = validateValue(value) ? {
    [valueName]: value
  } : {};
  return <Component {...otherProps} {...altProps} />;
};
export const withDependentReferenceDataCustomField = (valueName, validateValue) => compose(_withDependentReferenceDataCustomField(valueName, validateValue), withDependentReferenceData);